class FeatureGalleryDialog {
  constructor() {
    this.animationDuration = 240; 
    this.activeTrigger = null; 
    
    // 綁定唯一的 Dialog 與內容容器
    this.dialog = document.getElementById('shared-feature-dialog');
    this.dialogContent = document.getElementById('shared-dialog-content');
    
    if (this.dialog && this.dialogContent) {
      this.initDialog();
      document.body.addEventListener('click', this.handleTriggerClick.bind(this));
    }
  }

  initDialog() {
    // 針對唯一的 Dialog 加上監聽事件
    this.dialog.addEventListener('close', (event) => {
      event.preventDefault();
      this.close(this.dialog);
    });

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) {
        this.close(this.dialog);
      }
    });

    const closeButton = this.dialog.querySelector('.dialog-close-button');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.close(this.dialog);
      });
    }
  }

  lockScroll() {
    if (document.documentElement.classList.contains('has-dialog-open')) return;
    const isScrollable = document.documentElement.scrollHeight > document.documentElement.clientHeight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (isScrollable && scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    document.documentElement.classList.add('has-dialog-open');
  }

  unlockScroll() {
    document.documentElement.classList.remove('has-dialog-open');
    document.body.style.paddingRight = '';
  }

  handleTriggerClick(event) {
    const trigger = event.target.closest('.card-dialog-trigger');
    if (trigger) {
      event.preventDefault();
      this.show(trigger);
    }
  }

  show(trigger) {
    if (!this.dialog) return;

    // 1. 取得對應的 template
    const templateId = trigger.getAttribute('data-template-id');
    const template = document.getElementById(templateId);
    if (!template) return;

    this.activeTrigger = trigger; 

    // 2. 清空先前的內容並注入新的模板內容
    this.dialogContent.innerHTML = '';
    this.dialogContent.appendChild(template.content.cloneNode(true));

    // 3. 鎖定滾動並顯示 Modal
    this.lockScroll();
    this.dialog.removeAttribute('hidden');
    this.dialog.showModal();
  }

  close(dialog) {
    if (dialog.classList.contains('is-closing')) return;

    dialog.classList.add('is-closing');

    // 用來儲存 setTimeout 的變數，以便稍後可以清除它
    let fallbackTimeout;

    // 將處理函式獨立出來，這樣才能確實地 removeEventListener
    const onTransitionEnd = (event) => {
      // 【關鍵修復 1】：過濾掉子元素（例如關閉按鈕 hover）的 transitionend 冒泡事件
      // 確保只有 dialog 本身，或是 dialog__box 的動畫結束時，才執行關閉邏輯
      if (event && event.target !== dialog && !event.target.classList.contains('feature-card-dialog__box')) {
        return;
      }

      // 【關鍵修復 2】：明確移除監聽器，避免殘留到下一次開啟
      dialog.removeEventListener('transitionend', onTransitionEnd);
      clearTimeout(fallbackTimeout);

      dialog.classList.remove('is-closing');
      dialog.close();
      dialog.setAttribute('hidden', '');
      this.unlockScroll();

      // 關閉後清空內容，釋放記憶體中的 DOM 節點與圖片
      setTimeout(() => {
        if (this.dialogContent) this.dialogContent.innerHTML = '';
      }, 50);

      // 無障礙體驗：焦點歸還給原本觸發的卡片按鈕
      if (this.activeTrigger) {
        this.activeTrigger.focus();
        this.activeTrigger = null;
      }
    };

    // 監聽動畫結束事件 (改為在函式內手動解除)
    dialog.addEventListener('transitionend', onTransitionEnd);

    // 備用機制：如果過了設定的時間 (動畫時間 + 50ms 緩衝) transitionend 沒觸發，強制關閉
    fallbackTimeout = setTimeout(() => {
      if (dialog.classList.contains('is-closing')) {
        onTransitionEnd(); // 這裡呼叫時沒有 event 參數，會直接進入關閉邏輯
      }
    }, this.animationDuration + 50);
  }
}

class FeatureGalleryKeyboardNav {
  constructor(galleryElement) {
    this.gallery = galleryElement;
    this.container = this.gallery.querySelector('.card-set');
    this.items = Array.from(this.container.querySelectorAll('.gallery-item'));
    this.scrollComponent = this.gallery.closest('scroll-carousel');
    
    if (!this.container || this.items.length === 0) return;

    this.init();
  }

  init() {
    this.container.setAttribute('tabindex', '0');

    this.items.forEach((item, index) => {
      const focusableElement = item.querySelector('button.card-dialog-trigger');
      if(focusableElement) {
        focusableElement.setAttribute('tabindex', index === 0 ? '0' : '-1');
      }
    });
    
    this.container.addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  handleKeyDown(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }

    event.preventDefault();

    const currentItem = document.activeElement.closest('.gallery-item');
    let currentIndex = this.items.indexOf(currentItem);

    if (currentIndex === -1) {
        const selectedItem = this.container.querySelector('.is-selected') || this.items[0];
        currentIndex = this.items.indexOf(selectedItem);
    }

    let nextIndex;
    if (event.key === 'ArrowRight') {
      nextIndex = Math.min(this.items.length - 1, currentIndex + 1);
    } else {
      nextIndex = Math.max(0, currentIndex - 1);
    }

    if (nextIndex !== currentIndex) {
      this.focusItem(nextIndex);
    }
  }
  
  focusItem(index) {
    const nextItem = this.items[index];
    if (!nextItem) return;

    const nextFocusableElement = nextItem.querySelector('button.card-dialog-trigger');
    if (nextFocusableElement) {
        nextFocusableElement.focus();
    }
    
    this.items.forEach((item, i) => {
        const focusable = item.querySelector('button.card-dialog-trigger');
        if (focusable) {
            focusable.setAttribute('tabindex', i === index ? '0' : '-1');
        }
    });

    if (this.scrollComponent && typeof this.scrollComponent.scrollToIndex === 'function') {
      this.scrollComponent.scrollToIndex(index);
    } else {
      nextItem.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  }
}

function lazyInit() {
  const gallery = document.querySelector('.gallery-feature-cards');
  if (!gallery) return;

  const obs = new IntersectionObserver(
    ([entry], observer) => {
      if (entry.isIntersecting) {
        customElements.whenDefined('scroll-carousel').then(() => {
          gallery.init?.();
        });
        new FeatureGalleryDialog();
        new FeatureGalleryKeyboardNav(gallery); 
        observer.disconnect();
      }
    },
    { rootMargin: '200px' }
  );

  obs.observe(gallery);
}

lazyInit();